import { Check, LoaderCircle, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";

export type ActionState = "idle" | "pending" | "success" | "error";

/** A settled request, rather than elapsed time, determines the result. */
export function useAsyncAction() {
  const [state, setState] = useState<ActionState>("idle");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const mounted = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; clearTimeout(timer.current); }; }, []);
  async function run(action: () => Promise<unknown>) {
    if (busy.current) return;
    busy.current = true;
    clearTimeout(timer.current);
    setError(""); setState("pending");
    try {
      const result = await action();
      if (mounted.current) {
        setState(result === false ? "idle" : "success");
        timer.current = setTimeout(() => { if (mounted.current) setState("idle"); }, 2200);
      }
    } catch (cause) {
      if (mounted.current) { setError(cause instanceof Error ? cause.message : "Не удалось выполнить действие. Попробуйте ещё раз."); setState("error"); }
    } finally { busy.current = false; }
  }
  return { state, error, run };
}

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  children: ReactNode;
  action?: () => Promise<unknown>;
  state?: ActionState;
  error?: string;
  pendingLabel?: string;
  successLabel?: string;
  iconOnly?: boolean;
};

export function StatefulButton({ children, action, state: controlledState, error: controlledError, pendingLabel = "Сохраняем…", successLabel = "Сохранено", iconOnly = false, className = "", disabled, onClick, ...props }: Props) {
  const local = useAsyncAction();
  const state = controlledState ?? local.state;
  const error = controlledError ?? local.error;
  const [showPending, setShowPending] = useState(false);
  useEffect(() => {
    setShowPending(false);
    if (state !== "pending") return;
    const delay = setTimeout(() => setShowPending(true), 300);
    return () => clearTimeout(delay);
  }, [state]);
  const visualState = state === "pending" && !showPending ? "idle" : state;
  const label = visualState === "pending" ? pendingLabel : visualState === "success" ? successLabel : visualState === "error" ? "Повторить" : children;
  return <>
    <button {...props} type={props.type || "button"} className={`stateful-button ${className}`} data-state={state}
      disabled={disabled || state === "pending"} aria-busy={state === "pending"}
      aria-label={iconOnly && visualState !== "idle" ? String(label) : props["aria-label"]}
      title={error || props.title}
      onClick={event => { onClick?.(event); if (action && !event.defaultPrevented) void local.run(action); }}>
      <span className="stateful-content" aria-live="polite">
        <span className="stateful-reserve" aria-hidden="true">{children}</span>
        {!iconOnly && <><span className="stateful-reserve" aria-hidden="true">{pendingLabel}</span><span className="stateful-reserve" aria-hidden="true">{successLabel}</span></>}
        <span className="stateful-visible">{visualState === "idle" ? children : <>{visualState === "pending" ? <LoaderCircle className="action-spinner" size={17} /> : state === "success" ? <Check size={17} /> : <AlertCircle size={17} />}{!iconOnly && label}</>}</span>
      </span>
    </button>
    {error && <span className="action-error" role="alert">{error}</span>}
  </>;
}
